import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ReactComponent as SettingIcon } from '../../assets/icons/setting-icon.svg';
import { useAppBranding } from '../../config/branding';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import MyButton from '../../shared-components/button/Button';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import TabToggle from '../../shared-components/tab-toggle/TabToggle';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import penaltyManagementApi from '../../utils/api/penalty-management';

import GlobalSetting from './components/GlobalSetting';
import OverrideListPages from './components/OverrideListPages';
import RequestList from './components/request/RequestList';
import TransactionList from './components/TransactionList';

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
}
interface RevenueSummary {
  totalBaseAmount: number;
  totalPenaltyAmount: number;
  totalRevenue: number;
}

interface DurationAmountListInterface {
  from: number;
  to: number;
  mode: string;
  amount: number;
}

interface QuickViewDataInterface {
  ceilingType: string;
  ceilingValue: number;
  triggerMessage: string[];
  effectiveFrom: string;
  effectiveTo: string;
  dynamicPricing: string[];
  durationAmountList: DurationAmountListInterface[];
}

/**
 * Universal text formatter for UI
 * @param {any} value - The value
 */
const formatText = (value?: string | string[]) => {
  if (!value) return '';

  const text = Array.isArray(value) ? value.join(', ') : value;

  return (
    text
      // replace snake_case & kebab-case
      .replace(/[_-]/g, ' ')
      // add space before capital letters (camelCase / PascalCase)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // ensure space after commas
      .replace(/,/g, ', ')
      // capitalize each word
      .replace(/\b\w/g, (char) => char.toUpperCase())
      // remove extra spaces
      .replace(/\s+/g, ' ')
      .trim()
  );
};

/**
 * Penalty Management pages
 * @returns {any} - JSX.Element
 */
function PenaltyManagement() {
  const branding = useAppBranding();
  const { t } = useTranslation('penaltyManagement');
  const dispatch = useAppDispatch();
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { currentPage } = useParams<{ currentPage: string }>();
  const [openTab, setOpenTab] = useState<number>(1);
  const [quickViewData, setQuickViewData] = useState<QuickViewDataInterface | null>(null);
  const [modalView, setModalView] = useState<ModalInterface>({
    title: '',
    header: false,
    content: <div />,
    rightCorner: 'ok',
    notification: false,
  });
  const [revenueData, setRevenueData] = useState<RevenueSummary>({
    totalBaseAmount: 0,
    totalPenaltyAmount: 0,
    totalRevenue: 0,
  });

  const durationList = quickViewData?.durationAmountList || [];
  const firstDuration = durationList[0];
  const remainingDurations = durationList.slice(1);

  /**
   * Fetch global setting quick view data
   */
  const fetchData = async () => {
    try {
      const response = await penaltyManagementApi.globalSettingQuickView();
      setQuickViewData(response.data);
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; code?: string }>;
      dispatch(
        setNotificationMessage({
          message: axiosError?.response?.data?.message || 'Something went wrong!',
          status: axiosError?.response?.status || 500,
          type: 'error',
          code: axiosError?.response?.data?.code,
        }),
      );
    }
  };

  // Set tab based on URL
  useEffect(() => {
    if (currentPage) {
      let number = 0;
      switch (currentPage) {
        case 'approved':
          number = 0;
          break;
        case 'unapproved':
          number = 1;
          break;
        case 'rejected':
          number = 2;
          break;
        default:
          number = 0;
      }
      setOpenTab(number);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchData();
    dispatch(setPageTitle(t('idleFee')));
  }, [t]);

  /**
   * Change tab + update URL
   * @param {string} tabLabel - The label of the tab
   */
  const changeTabPages = (tabLabel: string) => {
    const index = [t('overrides'), t('request'), t('transaction')].indexOf(tabLabel);
    setOpenTab(index);

    let path = '';

    switch (index) {
      case 0:
        path = 'overrides';
        break;
      case 1:
        path = 'request';
        break;
      case 2:
        path = 'transaction';
        break;
      default:
        path = 'overrides';
    }

    window.history.pushState({}, '', path);
  };

  /**
   * Modal box close function
   * @param {any} message - The message
   */
  const closeModalFunction = (message: string) => {
    if (message) {
      setOpenStatus(true);
      setModalView({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
            }}
          />
        ),
        rightCorner: '',
        notification: true,
      });
    } else {
      setOpenStatus(false);
    }
  };

  /**
   * AddOverrides
   */
  const handleGlobalSetting = () => {
    setOpenStatus(true);
    setModalView({
      title: t('globalSettings'),
      header: false,
      content: <GlobalSetting functionCall={fetchData} closeModal={closeModalFunction} />,
      rightCorner: '',
      notification: false,
    });
  };
  /**
   * Fetching card list api
   */
  const handleCardListApi = async () => {
    try {
      const response = await penaltyManagementApi.fetchCardViewPrices();

      const { totalBaseAmount, totalPenaltyAmount } = response?.data || {};

      setRevenueData({
        totalBaseAmount: totalBaseAmount ?? 0,
        totalPenaltyAmount: totalPenaltyAmount ?? 0,
        totalRevenue: (totalBaseAmount ?? 0) + (totalPenaltyAmount ?? 0),
      });
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: error?.response?.status || 500,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  useEffect(() => {
    handleCardListApi();
  }, []);
  return (
    <div className="penalty-management-main">
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
        position="bottom-right"
      />
      <div className="global-setting-icon">
        <div>
          <MyButton
            label={t('globalSettings')}
            svgIcon={<SettingIcon />}
            buttonType="none"
            onClickFunc={handleGlobalSetting}
          />
        </div>
      </div>

      <div className="revenue-content">
        <div className="revenue-table">
          <p className="title">{t('baseRevenue')}</p>
          <div className="value-content">
            <p className="value">{ToFixedValue(revenueData.totalBaseAmount)}</p>
            <p className="icon">{branding.text.currency}</p>
          </div>
        </div>

        <div className="revenue-table">
          <p className="title">{t('penaltyRevenue')}</p>
          <div className="value-content">
            <p className="value">{ToFixedValue(revenueData.totalPenaltyAmount)}</p>
            <p className="icon">{branding.text.currency}</p>
          </div>
        </div>

        <div className="revenue-table">
          <p className="title">{t('totalRevenue')}</p>
          <div className="value-content">
            <p className="value">{ToFixedValue(revenueData.totalRevenue)}</p>
            <p className="icon">{branding.text.currency}</p>
          </div>
        </div>
      </div>

      <div className="global-settings-quick-view">
        <p className="title">{t('globalSettingsQuickView')}</p>
        <div className="quick-view">
          <div className="quick-view-item">
            <span className="label">{t('ceilingType')}</span>
            <span className="value">{formatText(quickViewData?.ceilingType || 'N/A')}</span>
          </div>
          <div className="quick-view-item">
            <span className="label">{t('maxCeilingValue')}</span>
            <span className="value">{quickViewData?.ceilingValue ?? 0}</span>
          </div>
          <div className="quick-view-item">
            <span className="label">{t('triggerMessage')}</span>
            <span className="value">
              {formatText(quickViewData?.triggerMessage?.join(', ')) || 'N/A'}
            </span>
          </div>
          <div className="quick-view-item">
            <span className="label">{t('effectiveFrom')}</span>
            <span className="value">
              {quickViewData?.effectiveFrom ? ConvertUTCtoDate(quickViewData.effectiveFrom) : 'N/A'}
            </span>
          </div>
          <div className="quick-view-item">
            <span className="label">{t('effectiveTo')}</span>
            <span className="value">
              {quickViewData?.effectiveFrom ? ConvertUTCtoDate(quickViewData.effectiveTo) : 'N/A'}
            </span>
          </div>
          <div className="quick-view-item">
            <span className="label">{t('dynamicPricing')}</span>
            <span className="value">
              {quickViewData?.dynamicPricing?.map(formatText).join(', ') || 'N/A'}
            </span>
          </div>

          <div className="duration-dropdown">
            {/* Trigger */}
            <div
              role="presentation"
              className={`duration-trigger ${isOpen ? 'open' : ''}`}
              onClick={() => setIsOpen((previous) => !previous)}>
              <div className="row">
                <div className="each-row">
                  <span className="label">{t('fromMins')}</span>
                  <span className="value">{firstDuration?.from}</span>
                </div>

                <div className="each-row">
                  <span className="label">{t('toMins')}</span>
                  <span className="value">{firstDuration?.to}</span>
                </div>

                <div className="each-row">
                  <span className="label">{t('mode')}</span>
                  <span className="value">{formatText(firstDuration?.mode)}</span>
                </div>

                <div className="each-row">
                  <span className="label">{t('amount')}</span>
                  <span className="value">
                    {firstDuration?.amount} {branding.text.currency}
                  </span>
                </div>
              </div>

              <span className={`arrow ${isOpen ? 'open' : ''}`} />
            </div>

            {/* Dropdown */}
            {isOpen && remainingDurations.length > 0 && (
              <div className="duration-menu">
                {remainingDurations.map((item, index) => (
                  <div className="menu-row" key={index}>
                    <div className="each-row">
                      <span className="label">{t('fromMins')}</span>
                      <span className="value">{item.from}</span>
                    </div>

                    <div className="each-row">
                      <span className="label">{t('toMins')}</span>
                      <span className="value">{item.to}</span>
                    </div>

                    <div className="each-row">
                      <span className="label">{t('mode')}</span>
                      <span className="value">{formatText(item.mode)}</span>
                    </div>

                    <div className="each-row">
                      <span className="label">{t('amount')}</span>
                      <span className="value">
                        {item.amount} {branding.text.currency}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <TabToggle
        tabs={[t('overrides'), t('request'), t('transaction')]}
        defaultTab={t('overrides')}
        onChange={(tab) => changeTabPages(tab)}
      />
      <div className="table-content">
        {openTab === 0 && (
          <div>
            <OverrideListPages />
          </div>
        )}
        {openTab === 1 && (
          <div>
            <RequestList />
          </div>
        )}
        {openTab === 2 && (
          <div>
            <TransactionList />
          </div>
        )}
      </div>
    </div>
  );
}

export default PenaltyManagement;
